import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Download, Trash2, Pencil, Plus, Search, Copy, ExternalLink } from "lucide-react";
import { PROVINCE_NAMES } from "@/lib/idProvinces";
import { getSchoolUrl } from "@/lib/slug";
import CurriculumMigrationPanel from "@/components/admin/CurriculumMigrationPanel";

/**
 * Kelola Sekolah — daftar sekolah + search & filter di atas.
 * State + handler tetap di <Admin />; komponen menerima props saja.
 */
export default function SchoolsTab({
  schools,
  totalCount,
  isAdminOrSuper,
  isSuper,
  onImport,
  onNewSchool,
  onEdit,
  onDuplicate,
  onDelete,
  query,
  onQueryChange,
  provinceFilter,
  onProvinceFilterChange,
  levelFilter,
  onLevelFilterChange,
  typeFilter,
  onTypeFilterChange,
}) {
  return (
    <TabsContent value="schools-mgmt" className="mt-6 space-y-3" data-testid="schools-mgmt-panel">
      {/* Temporary Super Admin action — one-off curriculum canonicalization. */}
      {isSuper && <CurriculumMigrationPanel />}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#4B5563] flex-1 min-w-0">
          Semua sekolah yang sudah dipublikasi. Klik <em>Edit</em> untuk memperbarui informasi.{" "}
          {isSuper ? "Hanya Super Admin yang bisa menghapus." : "Hubungi Super Admin untuk menghapus data sekolah."}
        </p>
        {isAdminOrSuper && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onImport} data-testid="import-schools-btn" className="rounded-full border-[#EAE7E0]">
              <Download size={14} className="mr-1 rotate-180" /> Impor CSV
            </Button>
            <Button onClick={onNewSchool} data-testid="new-school-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white">
              <Plus size={14} className="mr-1" /> Sekolah baru
            </Button>
          </div>
        )}
      </div>

      {/* Search + filter row */}
      <div className="bg-white border border-[#EAE7E0] rounded-2xl p-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Cari</div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
            <Input
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Nama sekolah, kota, atau NPSN…"
              data-testid="schools-search-input"
              className="pl-9 h-9"
            />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Provinsi</div>
          <Select value={provinceFilter} onValueChange={onProvinceFilterChange}>
            <SelectTrigger data-testid="schools-province-filter" className="w-48 h-9"><SelectValue placeholder="Semua provinsi" /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">Semua provinsi</SelectItem>
              {PROVINCE_NAMES.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Jenjang</div>
          <Select value={levelFilter} onValueChange={onLevelFilterChange}>
            <SelectTrigger data-testid="schools-level-filter" className="w-32 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua jenjang</SelectItem>
              <SelectItem value="TK">TK</SelectItem>
              <SelectItem value="SD">SD</SelectItem>
              <SelectItem value="SMP">SMP</SelectItem>
              <SelectItem value="SMA">SMA</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Tipe</div>
          <Select value={typeFilter} onValueChange={onTypeFilterChange}>
            <SelectTrigger data-testid="schools-type-filter" className="w-32 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua tipe</SelectItem>
              <SelectItem value="negeri">Negeri</SelectItem>
              <SelectItem value="swasta">Swasta</SelectItem>
              <SelectItem value="internasional">Internasional</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-[#4B5563] pb-2 ml-auto" data-testid="schools-count-badge">
          {totalCount.toLocaleString("id-ID")} sekolah
        </div>
      </div>

      {schools.length === 0 && (
        <div className="bg-white border border-[#EAE7E0] rounded-2xl p-6 text-center text-sm text-[#4B5563]" data-testid="schools-empty-filter">
          Tidak ada sekolah cocok dengan pencarian ini.
        </div>
      )}
      {schools.map((s, i) => (
        <div key={s.school_id} data-testid={`school-row-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-4 flex items-center gap-4">
          {s.image_url && <img src={s.image_url} alt="" className="w-16 h-16 rounded-xl object-cover" />}
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-[#4B5563] flex items-center gap-2 flex-wrap">
              {s.city} · {s.school_type}
              {s.brand_id && (
                <span data-testid={`school-brand-badge-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E3A8A] text-[10px] uppercase tracking-wider border border-[#DBEAFE]">
                  {s.brand_id}{s.branch_name ? ` · ${s.branch_name}` : ""}
                </span>
              )}
              {s.education_levels && s.education_levels.length > 0 && (
                <span data-testid={`school-levels-badge-${i}`} className="text-[10px] normal-case tracking-normal text-[#4B5563]">{s.education_levels.join(" · ")}</span>
              )}
            </div>
            <div className="font-serif-display text-lg text-[#1F2937] truncate">{s.name}</div>
            <div className="text-xs text-[#4B5563]">{s.review_count} review · rating {s.avg_rating || 0}</div>
          </div>
          <Button variant="outline" onClick={() => onEdit(s)} data-testid={`edit-school-${i}`} className="rounded-full border-[#EAE7E0]">
            <Pencil size={14} className="mr-1" /> Edit
          </Button>
          <a href={getSchoolUrl(s)} target="_blank" rel="noreferrer" data-testid={`view-public-school-${i}`}
            className="inline-flex items-center px-3 h-9 rounded-full border border-[#EAE7E0] text-sm text-[#1F2937] hover:border-[#1E3A8A] hover:text-[#1E3A8A]">
            <ExternalLink size={14} className="mr-1" /> Halaman Publik
          </a>
          {isAdminOrSuper && (
            <Button variant="outline" onClick={() => onDuplicate(s)} data-testid={`duplicate-school-${i}`}
              className="rounded-full border-[#EAE7E0] text-[#1E3A8A] hover:border-[#1E3A8A]">
              <Copy size={14} className="mr-1" /> Duplikat
            </Button>
          )}
          <Link to={`/admin/analytics/${s.school_id}`} data-testid={`analytics-school-${i}`}
            className="inline-flex items-center px-3 h-9 rounded-full border border-[#EAE7E0] text-sm text-[#1F2937] hover:border-[#1E3A8A] hover:text-[#1E3A8A]">
            <BarChart3 size={14} className="mr-1" /> Analytics
          </Link>
          {isSuper && (
            <Button variant="outline" onClick={() => onDelete(s.school_id)} data-testid={`delete-school-${i}`} className="rounded-full border-[#EAE7E0] text-[#B91C1C]">
              <Trash2 size={14} className="mr-1" /> Hapus
            </Button>
          )}
        </div>
      ))}
    </TabsContent>
  );
}
